import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function AdminSponsorRefresh({ roles = [], onImported }) {
  const [schedule, setSchedule] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const allowed = roles.some((role) => ["super_admin", "tenant_owner", "tenant_admin", "compliance_reviewer"].includes(role));
  useEffect(() => {
    if (allowed) api.get("/sponsors/schedule").then(({ data }) => setSchedule(data))
      .catch(() => setMessage("Unable to load the register refresh status."));
  }, [allowed]);
  if (!allowed) return null;
  const refresh = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sponsors/refresh");
      if (data.version_id && data.status === "awaiting_approval") {
        const version = (await api.get(`/sponsors/imports/${data.version_id}/report`)).data;
        onImported(version);
      }
      setMessage(data.status === "awaiting_approval" ? "New register downloaded. Review the changes below before publishing."
        : data.status === "unchanged" ? "The official file is already imported. Review any version awaiting approval in the history below."
        : "A register check is already running. Try again shortly.");
      setSchedule((await api.get("/sponsors/schedule")).data);
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Unable to download the official register.");
    } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true);
    try {
      const enabled = schedule?.integration_status !== "connected";
      setSchedule((await api.post("/sponsors/schedule", { enabled })).data);
      setMessage(enabled ? "Daily downloads enabled. Each new version still needs approval." : "Daily downloads disabled.");
    } catch (error) { setMessage(error?.response?.data?.detail || "Unable to update daily downloads."); }
    finally { setBusy(false); }
  };
  return <div className="card p-6" data-testid="sponsor-refresh-panel">
    <h2 className="text-lg font-semibold">Official register refresh</h2>
    <p className="mt-2 text-sm text-slate-600">Download the latest worker sponsor register from GOV.UK. Your published directory stays available while the new version awaits review.</p>
    <p className="mt-2 text-sm">Daily downloads: {schedule ? schedule.integration_status === "connected" ? "Enabled" : "Disabled" : "Checking…"}</p>
    {schedule?.job && <p className="mt-2 text-xs text-slate-500">Last check: {schedule.job.last_run_at ? new Date(schedule.job.last_run_at).toLocaleString("en-GB") : "Not yet run"} · {schedule.job.status?.replace(/_/g, " ")}</p>}
    {schedule?.job?.last_error && <p role="alert" className="mt-2 text-sm text-amber-800">{schedule.job.last_error}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      <button className="btn btn-primary" disabled={busy} onClick={refresh}>Check GOV.UK now</button>
      <button className="btn btn-ghost" disabled={busy || !schedule} onClick={toggle}>{schedule?.integration_status === "connected" ? "Disable daily downloads" : "Enable daily downloads"}</button>
    </div>
    {message && <p role="status" className="mt-3 text-sm">{message}</p>}
  </div>;
}
